import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import Form from '../../../components/Form'
import Field from '../../../components/Form/Field'
import RadioBlock from '../../../components/Form/RadioBlock'
import DrawerHeadline from '../../../components/Drawer/DrawerHeadline'
import AdQuarantineField from '../../ReusableFormFields/AdQuarantineField'
import AudiencesInfoSection from '../../ReusableFormFields/Audiences/AudiencesFields/AudiencesInfoSection'

import { usePurifiedFormik } from '../../../hooks/formHooks/usePurifiedFormik'
import useIsAdvancedPartnership from '../../../features/hooks/useIsAdvancedPartnership'

import { selectedPartnershipSelector } from '../../../modules/selectors/partnerships'

import { AUDIENCE, AUDIENCE_OPTION, getInitialValues, validationSchema } from './fields'
import { OPTION_NO, OPTION_YES } from '../../../constants/forms'

import useDrawerFormStyles from '../../../styles/common/drawerForms'

const GoogleAudienceForm = ({
  formName,
  handleSubmit,
  errorSelector,
  isLoadingSelector,
  audienceWasCreated,
  onSuccessSubmit,
  clearHandler,
  audiencesIsLoading,
  isInternalAssetGroupCreation,
  audiencesList,
  formProgressItems
}) => {
  const drawerFormClasses = useDrawerFormStyles()
  const { t } = useTranslation()

  const [successSubmit, setSuccessSubmit] = useState(false)

  const selectedPartnership = useSelector(selectedPartnershipSelector)

  const isAdvancedPartnership = useIsAdvancedPartnership()

  const { rights_holder_name: rightsHolderName } = selectedPartnership

  const onSubmit = useCallback(
    values => {
      if (values[AUDIENCE_OPTION] === OPTION_NO) {
        // if no audiences option is selected > don't send request
        setSuccessSubmit(true)
      } else {
        handleSubmit(values)
      }
    },
    [handleSubmit]
  )

  const initialValues = useMemo(() => getInitialValues(isInternalAssetGroupCreation), [isInternalAssetGroupCreation])
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { values, setFieldValue } = purifiedFormik

  return (
    <div className={drawerFormClasses.formContainer}>
      <DrawerHeadline
        title={`Do you want to target ${rightsHolderName} audiences?`}
        formProgressItems={formProgressItems}
      />
      <Form
        formName={formName}
        formik={purifiedFormik}
        successSubmit={successSubmit || audienceWasCreated}
        onSuccessSubmit={onSuccessSubmit}
        clearHandler={clearHandler}
        errorSelector={errorSelector}
        isLoadingSelector={isLoadingSelector}
      >
        {!isAdvancedPartnership && (
          <AudiencesInfoSection targetAudiencesText="Target one audience which can contain multiple audience segments" />
        )}
        <RadioBlock
          setFieldValue={setFieldValue}
          id="radio_audience_option_yes"
          name={AUDIENCE_OPTION}
          value={OPTION_YES}
          selectedValue={values[AUDIENCE_OPTION]}
          label={t('Yes')}
        >
          <Field
            placeholder="Select an audience"
            options={audiencesList}
            name={AUDIENCE}
            formik={purifiedFormik}
            isLoading={audiencesIsLoading}
          />
        </RadioBlock>
        <RadioBlock
          setFieldValue={setFieldValue}
          id="radio_audience_option_no"
          name={AUDIENCE_OPTION}
          value={OPTION_NO}
          selectedValue={values[AUDIENCE_OPTION]}
          label={t('No')}
        />
        <AdQuarantineField formik={purifiedFormik} />
      </Form>
    </div>
  )
}

GoogleAudienceForm.propTypes = {
  formName: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  errorSelector: PropTypes.func.isRequired,
  isLoadingSelector: PropTypes.func.isRequired,
  audienceWasCreated: PropTypes.bool,
  audiencesIsLoading: PropTypes.bool,
  audiencesList: PropTypes.array.isRequired,
  isInternalAssetGroupCreation: PropTypes.bool,
  onSuccessSubmit: PropTypes.func,
  clearHandler: PropTypes.func,
  formProgressItems: PropTypes.array
}

export default GoogleAudienceForm
